import {
  NotificationsApi,
  type NoticeHeaderCounter,
  type NoticeLink,
  NoticeDTO,
} from '@/entities/notifications/notification';
import { TreeComponent } from '@/shared/ui/tree';
import { Page } from '@/shared/api/pagination';

import { getHIColourByStatus } from '@/entities/monitoring/hi';
import { treeMap } from '@/shared/lib/utils/tree/tree-map';
import {
  ActionIcon,
  Anchor,
  Badge,
  Box,
  Button,
  Center,
  Collapse,
  CopyButton,
  Divider,
  Group,
  Notification,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import IconChevronDown from '@tabler/icons-react/dist/esm/icons/IconChevronDown.mjs';
import IconCopy from '@tabler/icons-react/dist/esm/icons/IconCopy.mjs';
import IconCopyCheck from '@tabler/icons-react/dist/esm/icons/IconCopyCheck.mjs';
import IconExclamationCircle from '@tabler/icons-react/dist/esm/icons/IconExclamationCircle.mjs';
import IconInfoCircle from '@tabler/icons-react/dist/esm/icons/IconInfoCircle.mjs';
import IconTrash from '@tabler/icons-react/dist/esm/icons/IconTrash.mjs';
import {
  type InfiniteData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import dayjs from 'dayjs';
import { isEqual, omit } from 'lodash';
import { memo, ReactNode, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useInRouterContext } from 'react-router-dom';
import { getCIHealthURL } from '@/features/srm-view';
import { getCIBulkImportURL } from '@/entities/srm/ci-bulk-import';
import { EVENT_ICONS } from '../config/event-icons';
import { useNotificationsLogStore } from '../model/notification-log-store';

interface NodeProps {
  label: ReactNode;
  icon: string;
  icon_fill: { type: string; value: string };
  timestamp: number;
  annotations: any;
  link?: NoticeLink | null;
}

/**
 * Переход по ссылке уведомления. Уведомление показывается и
 * всплывашкой, которая живёт выше роутера, поэтому там ссылка обычная, а в
 * шторке переход идёт роутером; шторка при этом закрывается, иначе останется
 * поверх открытой страницы.
 */
const NoticeNodeLink = ({
  link,
  children,
  testId,
}: {
  link: NoticeLink;
  children: ReactNode;
  testId?: string;
}) => {
  const setOpened = useNotificationsLogStore(({ setOpened }) => setOpened);
  const inRouterContext = useInRouterContext();

  const isImport = 'type' in link;
  const url = isImport
    ? getCIBulkImportURL(link.job_id)
    : link.level === 'hi'
      ? getCIHealthURL({
          ciId: link.ci_id,
          tqlHiLinkId: link.tql_hi_link_id ?? undefined,
          hiTypeName: link.hi_type_name ?? undefined,
        })
      : getCIHealthURL({ ciId: link.ci_id });

  const handleClick = useCallback(() => setOpened(false), [setOpened]);
  const linkTestId =
    testId ?? (isImport ? 'notice-ci-import-link' : 'notice-ci-health-link');

  return inRouterContext ? (
    <Anchor
      component={Link}
      to={url}
      inherit
      underline={'hover'}
      onClick={handleClick}
      data-testid={linkTestId}
    >
      {children}
    </Anchor>
  ) : (
    <Anchor
      href={url}
      inherit
      underline={'hover'}
      onClick={handleClick}
      data-testid={linkTestId}
    >
      {children}
    </Anchor>
  );
};

const RenderNode = memo((props: NodeProps) => {
  const { label, icon, icon_fill, timestamp, annotations, link } = props;
  const { i18n, t } = useTranslation();

  const [opened, setOpened] = useState(false);
  const Icon = EVENT_ICONS[icon] || IconExclamationCircle;

  return (
    <Stack className={'animate-fade overflow-hidden'}>
      <Group
        gap={'xs'}
        className={'overflow-hidden'}
        wrap={'nowrap'}
        align={'center'}
      >
        <ThemeIcon
          autoContrast
          color={
            icon_fill?.type === 'severity'
              ? getHIColourByStatus(icon_fill?.value)
              : icon_fill?.value || 'blue'
          }
        >
          {icon && <Icon />}
        </ThemeIcon>
        {timestamp && (
          <Group className={'shrink-0'} gap={'xs'}>
            <Text
              c={'dimmed'}
              size={'xs'}
              component={'div'}
              className={'!leading-[28px]'}
            >
              {dayjs(timestamp)
                .locale(i18n.language)
                .format('DD MMM YY HH:mm ')}
            </Text>
            <Divider orientation={'vertical'} my={5} />
          </Group>
        )}

        <Group className={'grow overflow-hidden'} wrap={'nowrap'}>
          <Tooltip label={label}>
            <Text
              size={'sm'}
              className={'overflow-hidden grow'}
              truncate={'end'}
            >
              {link ? (
                <NoticeNodeLink link={link}>{label}</NoticeNodeLink>
              ) : (
                label || '...'
              )}
            </Text>
          </Tooltip>

          <CopyButton value={label['props']['children']} timeout={750}>
            {({ copied, copy }) => (
              <Tooltip label={t('action.copy')}>
                <Tooltip label={t('text.copied')} opened={copied}>
                  <ActionIcon
                    variant={'light'}
                    color={copied ? 'teal' : 'blue'}
                    onClick={copy}
                    size={'sm'}
                  >
                    {copied ? (
                      <IconCopyCheck size={16} />
                    ) : (
                      <IconCopy size={16} />
                    )}
                  </ActionIcon>
                </Tooltip>
              </Tooltip>
            )}
          </CopyButton>
        </Group>

        {annotations && (
          <ActionIcon
            size={'sm'}
            variant={'light'}
            component={'div'}
            onClick={() => setOpened(!opened)}
          >
            <IconInfoCircle />
          </ActionIcon>
        )}
      </Group>
      <Collapse expanded={opened} className={'overflow-hidden'}>
        <Stack>
          <Tooltip label={annotations?.summary} multiline w={550} withArrow>
            <Text size={'xs'} lineClamp={4} className={'whitespace-pre-wrap!'}>
              {annotations?.summary}
            </Text>
          </Tooltip>
          <Tooltip label={annotations?.description} multiline w={550} withArrow>
            <Text size={'xs'} lineClamp={4} className={'whitespace-pre-wrap!'}>
              {annotations?.description}
            </Text>
          </Tooltip>
        </Stack>
      </Collapse>
    </Stack>
  );
});

const HEADER_COUNTER_COLORS: Record<NoticeHeaderCounter['kind'], string> = {
  total: 'blue',
  created: 'green',
  modified: 'blue',
  unchanged: 'gray',
  skipped: 'yellow',
  deleted: 'violet',
  not_found: 'orange',
  errors: 'red',
  links_created: 'cyan',
};

export const TitleRender = memo(
  ({ data, created_at }: Pick<NoticeDTO, 'data' | 'created_at'>) => {
    const { i18n, t } = useTranslation();
    const hasHeaderCounters = Array.isArray(data?.header_counters);

    const countElements = useMemo(() => {
      if (hasHeaderCounters) {
        return data.header_counters
          .filter(
            ({ kind, value }) =>
              kind in HEADER_COUNTER_COLORS &&
              Number.isFinite(value) &&
              value >= 0,
          )
          .map(({ kind, value }) => {
            const label = `${t(`log.counters.${kind}`, { ns: 'notifications' })}: ${value.toLocaleString(i18n.language)}`;
            const props = {
              'data-testid': `notice-header-counter-${kind}`,
              'aria-label': label,
              tabIndex: 0,
            };
            return (
              <Tooltip
                key={kind}
                label={label}
                withArrow
                events={{ hover: true, focus: true, touch: false }}
              >
                {kind === 'total' ? (
                  <Badge
                    variant='light'
                    size='sm'
                    className='shrink-0'
                    classNames={{ label: 'min-w-fit' }}
                    {...props}
                  >
                    {value}
                  </Badge>
                ) : (
                  <Text
                    component='span'
                    size='xs'
                    c={HEADER_COUNTER_COLORS[kind]}
                    fw={600}
                    className='shrink-0 ml-1 tabular-nums'
                    {...props}
                  >
                    {value}
                  </Text>
                )}
              </Tooltip>
            );
          });
      }

      const subCounts = [
        {
          key: 'count_firing',
          value: data?.count_firing,
          color: 'red',
          prefix: '+',
        },
        {
          key: 'count_resolve',
          value: data?.count_resolve,
          color: 'green',
          prefix: '-',
        },
        {
          key: 'count_success',
          value: data?.count_success,
          color: 'green',
          prefix: '+',
        },
        {
          key: 'count_warning',
          value: data?.count_warning,
          color: 'yellow',
          prefix: '+',
        },
        {
          key: 'count_error',
          value: data?.count_error,
          color: 'red',
          prefix: '',
        },
      ] as const;

      const subElements = subCounts
        .filter(({ value }) => value != null && value > 0)
        .map(({ key, value, color, prefix }) => (
          <Text
            key={key}
            size={'xs'}
            c={color}
            fw={600}
            className='shrink-0 ml-1'
            data-testid={`notice-count-${key}`}
          >
            {prefix}
            {value}
          </Text>
        ));

      if (data?.count == null || data.count <= 0) return subElements;

      return [
        <Badge
          key='count'
          variant={'light'}
          size={'sm'}
          classNames={{ label: 'min-w-fit' }}
          data-testid='notice-count-total'
        >
          {data.count}
        </Badge>,
        ...subElements,
      ];
    }, [
      data?.count,
      data?.count_firing,
      data?.count_resolve,
      data?.count_success,
      data?.count_warning,
      data?.count_error,
      data?.header_counters,
      hasHeaderCounters,
      i18n.language,
      t,
    ]);

    return (
      <Group
        align={'center'}
        gap={'xs'}
        wrap={'nowrap'}
        className='overflow-hidden'
        data-testid='notice-header'
      >
        <Center>
          <Box>
            <Text c={'dimmed'} size={'xs'} inline>
              {dayjs(created_at).locale(i18n.language).format('HH:mm')}
            </Text>
          </Box>
        </Center>

        <Text
          truncate={'end'}
          className='overflow-hidden min-w-0'
          mah={250}
          data-testid='notice-header-title'
        >
          {data?.link ? (
            <NoticeNodeLink link={data.link} testId='notice-header-link'>
              {data?.header?.value || '...'}
            </NoticeNodeLink>
          ) : (
            data?.header?.value || '...'
          )}
        </Text>

        {countElements.length > 0 && (
          <Group
            gap={2}
            wrap='nowrap'
            align='center'
            className={
              hasHeaderCounters ? 'min-w-0 overflow-x-auto' : 'shrink-0'
            }
            data-testid='notice-header-counters'
          >
            {countElements}
          </Group>
        )}
      </Group>
    );
  },
);

export const IconRender = memo(({ data }: Pick<NoticeDTO, 'data'>) => {
  const Icon = EVENT_ICONS[data?.icon] || IconExclamationCircle;
  return (
    <ThemeIcon
      size={'xl'}
      autoContrast
      className={'pointer-events-all'}
      color={
        data?.icon_fill?.type === 'severity'
          ? getHIColourByStatus(data?.icon_fill?.value)
          : getHIColourByStatus(data?.icon_fill?.value) || 'blue'
      }
    >
      <Icon size={40} />
    </ThemeIcon>
  );
});
export const notificationClassNames = {
  root: 'spacing-xs !items-start',
  icon: 'shrink-0 !w-fit !h-fit self-start ',
  closeButton: '!h-fit self-start mt-[calc(var(--mantine-spacing-xs)*0.8)]',
  body: '!overflow-visible max-w-[600px]',
  description: '!overflow-visible !h-fit ',
  title: '!overflow-hidden max-w-[300px]',
};

export const getNotificationClassNames = (data: NoticeDTO['data']) =>
  Array.isArray(data?.header_counters)
    ? {
        ...notificationClassNames,
        body: `${notificationClassNames.body} min-w-0`,
        title: '!overflow-hidden !max-w-none',
      }
    : notificationClassNames;
export const ContentRender = memo(
  ({ data, onExpand }: { data: any; onExpand?: (state: boolean) => void }) => {
    const [opened, setOpened] = useState(false);

    const { t } = useTranslation();
    const [expandedState, setExpandedState] = useState({});

    const treeData = useMemo(() => {
      const expandedState = {};
      const mappedTreeData = treeMap(
        data.items,
        'items',
        'children',
        (x, { indexPath }) => {
          const pathString = indexPath.join('-');
          if (x.expanded) {
            expandedState[pathString] = true;
          }
          return {
            ...omit(x, 'items'),
            label: <>{x?.value?.value}</>,
            value: pathString,
          };
        },
      );
      setExpandedState(expandedState);
      return mappedTreeData;
    }, []);

    const handleExpand = () => {
      setOpened((prev) => {
        onExpand?.(!prev);
        return !prev;
      });
    };
    const handleRenderNode = useCallback(
      (props) => <RenderNode {...props} />,
      [],
    );

    return (
      <>
        <Button
          px={0}
          fullWidth
          size={'compact-sm'}
          variant={'transparent'}
          data-testid={'notification-content-toggle'}
          classNames={{
            root: '!h-fit ps-[calc(var(--mantine-spacing-xs)/2)]!',
            inner: '!justify-self-start ',
            section: `transition-transform ease-in-out duration-100 ${opened ? 'rotate-180' : ''}`,
          }}
          onClick={handleExpand}
          leftSection={<IconChevronDown size={16} />}
        >
          {data?.description?.value}
        </Button>
        <Box className='-ml-[calc(var(--mantine-spacing-xl)+var(--mantine-spacing-md))] overflow-hidden'>
          <Collapse expanded={opened} className='overflow-hidden'>
            {opened && (
              <>
                <Stack gap={0} mt={'xs'} className={'overflow-hidden'}>
                  <Text
                    size={'sm'}
                    lineClamp={4}
                    className={'whitespace-pre-wrap!'}
                  >
                    {data?.common_annotations?.summary}
                  </Text>
                  <Text
                    size={'sm'}
                    lineClamp={4}
                    className={'whitespace-pre-wrap!'}
                  >
                    {data?.common_annotations?.description}
                  </Text>
                </Stack>
                <Divider
                  label={t('log.state', { ns: 'notifications' })}
                  labelPosition={'left'}
                  py={'xs'}
                />

                <Stack mah={400} className='overflow-hidden grow'>
                  <TreeComponent
                    scrollAreaProps={{ offsetScrollbars: true }}
                    mah={500}
                    state={{
                      expanded: expandedState,
                    }}
                    onExpandedStateChange={setExpandedState}
                    data={treeData}
                    classNames={{
                      node: {
                        root: '!py-[calc(var(--mantine-spacing-xs)/2)] cursor-default overflow-hidden',
                        chevron: '!self-start mt-[3px]',
                      },
                    }}
                    renderNode={handleRenderNode}
                  />
                </Stack>
              </>
            )}
          </Collapse>
        </Box>
      </>
    );
  },
  (p, n) => isEqual(p.data, n.data),
);

export const useDeleteNotification = ({ onSuccess }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['delete-notice'],
    mutationFn: (id: string) =>
      NotificationsApi.deleteNotice(id).then((res) => res.data),
    onMutate: (id) => {
      queryClient.setQueryData<InfiniteData<Page<NoticeDTO>>>(
        ['notifications-log'],
        (p) => {
          return {
            ...p,
            pages:
              p?.pages?.map((x) => {
                const page = x.items?.find((y) => y.id === id);
                if (page) {
                  return {
                    ...x,
                    items: x.items?.filter((y) => y.id !== id) || [],
                  };
                }
                return x;
              }) || [],
          };
        },
      );
    },
    onSuccess: () => {
      onSuccess?.();
    },
  });
};
export const NotificationItem = memo(
  ({
    item,
    onDelete,
    onExpand,
  }: {
    item: NoticeDTO;
    onDelete: () => void;
    onExpand?: (state: boolean) => void;
  }) => {
    const { mutate } = useDeleteNotification({ onSuccess: onDelete });

    const closeButtonProps = useMemo(
      () => ({
        component: () => (
          <ActionIcon
            color={'red'}
            disabled={!item?.id}
            variant={'light'}
            onClick={() => mutate(item?.id)}
            className={'mt-[calc(var(--mantine-spacing-xs)*0.8)]'}
          >
            <IconTrash size={18} />
          </ActionIcon>
        ),
      }),
      [],
    );
    const handleClose = useCallback(
      (id: string) => () => {
        mutate(id);
      },
      [],
    );

    return (
      <Notification
        onClose={handleClose(item?.id)}
        classNames={getNotificationClassNames(item?.data)}
        color={'rgba(0,0,0,0)'}
        icon={<IconRender data={item?.data} />}
        title={<TitleRender data={item?.data} created_at={item?.created_at} />}
        withBorder
        closeButtonProps={closeButtonProps}
      >
        <ContentRender data={item?.data} onExpand={onExpand} />
      </Notification>
    );
  },
  (p, n) => p.item.id === n.item.id,
);
